import { resolveColorBoundary } from './colorBoundary';
/**
 * Кэш для измерения ширины текста (оптимизация производительности)
 */
const textWidthCache = new Map<string, number>();

/**
 * Измеряет ширину текста в canvas (с кэшированием)
 */
function measureTextWidth(
  text: string,
  font: string,
  ctx: CanvasRenderingContext2D
): number {
  const cacheKey = `${font}:${text}`;
  
  if (textWidthCache.has(cacheKey)) {
    return textWidthCache.get(cacheKey)!;
  }
  
  ctx.font = font;
  const width = ctx.measureText(text).width;
  
  // Ограничиваем размер кэша для экономии памяти
  if (textWidthCache.size > 1000) {
    const firstKey = textWidthCache.keys().next().value;
    textWidthCache.delete(firstKey);
  }
  
  textWidthCache.set(cacheKey, width);
  return width;
}

/**
 * Переносит текст на строки с учётом максимальной ширины
 */
export interface WrappedText {
  lines: string[];
  /** Столько первых строк покрашены целиком первым цветом. */
  primaryLines: number;
  /**
   * Сколько символов строки с индексом primaryLines ещё покрашены первым
   * цветом. Ноль — граница совпала с концом строки.
   *
   * Граница цвета проходит посреди строки, а не по её концу: в живом посте
   * Alash бирюзой залита фраза до двоеточия, а белый текст начинается в той же
   * строке сразу за ним. См. resolveColorBoundary.
   */
  splitChars: number;
}

/**
 * Переносит текст на строки с учётом максимальной ширины и ЯВНЫХ переносов.
 *
 * Раньше текст резался только по пробелам, и введённый редактором \n просто
 * прилипал к слову: заголовок в две строки склеивался в одну кашу. Теперь
 * перенос уважается, и заодно считается, где кончается первый цвет заголовка.
 */
/**
 * Нормализация заголовка перед раскладкой и показом: края, хвостовые пробелы
 * строк и повторные Enter не влияют на рендер (отзыв 04.09: «enter в конце
 * текста не должен влиять ни на что — и на посте, и на видео»). Правила ровно
 * те же, что в рилсовом wrapTextLines, — пост и рилс ведут себя одинаково.
 */
export function normalizeHeadline(text: string): string {
  return text
    .replace(/[ \t]+(\n|$)/g, '$1')
    .replace(/\n{2,}/g, '\n')
    .trim();
}

export function wrapTextWithBreaks(
  rawText: string,
  maxWidth: number,
  fontSize: number,
  fontFamily: string,
  ctx: CanvasRenderingContext2D
): WrappedText {
  const text = normalizeHeadline(rawText);
  // Кавычки обязательны: имена кастомных шрифтов содержат точки/запятые
  // (напр. "CustomFont_ofont.ru_Impact_…") → без кавычек CSS-шорткат невалиден,
  // canvas игнорит присваивание ctx.font и остаётся дефолт 10px → текст крошечный.
  const font = `${fontSize}px "${fontFamily}", sans-serif`;

  const wrapParagraph = (paragraph: string): string[] => {
    const words = paragraph.split(' ').filter(Boolean);
    const out: string[] = [];
    let currentLine = '';
    for (const word of words) {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      if (measureTextWidth(testLine, font, ctx) > maxWidth && currentLine) {
        out.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    }
    if (currentLine) out.push(currentLine);
    return out;
  };

  // Схлопываем повторные пробелы ДО поиска границы: строки потом склеиваются
  // одиночными пробелами, и только на такой нормализованной строке смещение
  // символа переносится в (номер строки, смещение внутри неё) один в один.
  const paragraphs = text.split(/\r?\n/).map((p) => p.split(' ').filter(Boolean).join(' '));
  const boundary = resolveColorBoundary(paragraphs.join('\n'));

  const lines: string[] = [];
  // Сколько символов нормализованного текста уже разложено по строкам.
  let consumed = 0;
  let primaryLines: number | null = null;
  let splitChars = 0;

  for (const paragraph of paragraphs) {
    const wrapped = paragraph === '' ? [''] : wrapParagraph(paragraph);
    for (const line of wrapped) {
      if (boundary !== null && primaryLines === null && boundary <= consumed + line.length) {
        primaryLines = lines.length;
        splitChars = Math.max(0, boundary - consumed);
      }
      lines.push(line);
      // +1 — пробел или перенос, съеденный при разбиении.
      consumed += line.length + 1;
    }
  }

  return {
    lines,
    // Границы нет — прежнее поведение: красится ровно первая строка.
    primaryLines: primaryLines ?? 1,
    splitChars: primaryLines === null ? 0 : splitChars,
  };
}

export function wrapText(
  text: string,
  maxWidth: number,
  fontSize: number,
  fontFamily: string,
  ctx: CanvasRenderingContext2D
): string[] {
  return wrapTextWithBreaks(text, maxWidth, fontSize, fontFamily, ctx).lines;
}

/**
 * Бинарный поиск оптимального fontSize
 * Работает БЕЗ зависимости от DOM (независимо от превью)
 */
export function calculateOptimalFontSize(
  text: string,
  maxWidth: number,
  maxHeight: number,
  fontFamily: string,
  ctx: CanvasRenderingContext2D,
  initialFontSize: number = 20
): { fontSize: number; lines: string[] } {
  const MIN_FONT_SIZE = 12;
  const MAX_FONT_SIZE = 80;
  const MAX_ITERATIONS = 15; // Ограничение для производительности
  const LINE_HEIGHT_MULTIPLIER = 1.2;

  let low = MIN_FONT_SIZE;
  let high = MAX_FONT_SIZE;
  let bestFontSize = initialFontSize;
  let bestLines: string[] = [];
  let iterations = 0;

  // ОПТИМИЗАЦИЯ: Быстрая проверка initialFontSize
  const initialLines = wrapText(text, maxWidth, initialFontSize, fontFamily, ctx);
  const initialHeight = initialLines.length * initialFontSize * LINE_HEIGHT_MULTIPLIER;
  
  if (initialHeight <= maxHeight) {
    // Проверяем, можем ли увеличить размер
    const largerSize = Math.min(initialFontSize * 1.2, MAX_FONT_SIZE);
    const largerLines = wrapText(text, maxWidth, largerSize, fontFamily, ctx);
    const largerHeight = largerLines.length * largerSize * LINE_HEIGHT_MULTIPLIER;
    
    if (largerHeight <= maxHeight) {
      // Можем увеличить, делаем бинарный поиск
      low = initialFontSize;
    } else {
      // initialFontSize оптимален
      console.log(`✅ Initial fontSize optimal: ${initialFontSize}px (skipped binary search)`);
      return { fontSize: initialFontSize, lines: initialLines };
    }
  } else {
    // Нужно уменьшить, делаем бинарный поиск
    high = initialFontSize;
  }

  // Бинарный поиск оптимального размера
  while (low <= high && iterations < MAX_ITERATIONS) {
    iterations++;
    const mid = Math.floor((low + high) / 2);
    const lines = wrapText(text, maxWidth, mid, fontFamily, ctx);
    const totalHeight = lines.length * mid * LINE_HEIGHT_MULTIPLIER;

    if (totalHeight <= maxHeight) {
      bestFontSize = mid;
      bestLines = lines;
      low = mid + 1; // Пробуем увеличить
    } else {
      high = mid - 1; // Нужно уменьшить
    }
  }

  console.log(`🔍 Optimal fontSize: ${bestFontSize}px (found in ${iterations} iterations)`);
  return { fontSize: bestFontSize, lines: bestLines };
}
